import {
    countRouted,
    DATA_KIND_ICON,
    DATA_KIND_PATH_LABEL,
    DATA_KINDS,
    getPaperPath,
    getPayerLanes,
    getTransactionCoverage,
    IPayerLane,
    IRoutableConnection,
    IRoutablePayer,
    isPaperOnlyKind,
} from '../../services/payer-routing';
import { ClearinghouseTransactionType } from '../../api';
import { PayerIdChip } from '../payer-id-chip/payer-id-chip';
import { PathChip, UNKNOWN_PATH } from '../payer-path/payer-path';
import { PayerTransactionTable } from '../payer-drawer/payer-transaction-table';
import {
    mdiArrowLeft,
    mdiLinkVariantOff,
    mdiArrowRight,
    mdiCheckCircleOutline,
    mdiMinus,
    mdiOfficeBuildingOutline,
    mdiShareVariantOutline,
    mdiSwapHorizontal,
} from '@mdi/js';
import Icon from '@mdi/react';
import { observer } from 'mobx-react';
import React from 'react';

import * as S from './payer-routing-panel.styles';

/**
 * The payer detail panel from the v2 design.
 *
 * Rendered as an expanded row rather than the design's drawer: row expansion is the pattern
 * this table already has, it keeps the payer next to its neighbours, and it avoids standing
 * up a drawer system for one screen. If the drawer proves to matter, this component is the
 * content and only the container changes.
 *
 * Everything reads the same derivation the list columns use — `getPayerLanes` and
 * `getPaperPath` — so a row and its open panel cannot contradict each other.
 */

/** The nine transactions in the design's grid, in its reading order. */
const EDI_GRID: Array<{ transaction: ClearinghouseTransactionType; label: string }> = [
    { transaction: ClearinghouseTransactionType.EDI837P, label: 'Professional claims' },
    { transaction: ClearinghouseTransactionType.EDI837I, label: 'Institutional claims' },
    { transaction: ClearinghouseTransactionType.EDI837D, label: 'Dental claims' },
    { transaction: ClearinghouseTransactionType.EDI270271, label: 'Eligibility checks' },
    { transaction: ClearinghouseTransactionType.EDI276277, label: 'Claim statuses' },
    { transaction: ClearinghouseTransactionType.EDI278, label: 'Authorizations' },
    { transaction: ClearinghouseTransactionType.EDI834, label: 'Benefit enrollment' },
    { transaction: ClearinghouseTransactionType.EDI835, label: 'Remittance (ERA)' },
    { transaction: ClearinghouseTransactionType.EDI275, label: 'Attachments (275)' },
];

const DIRECTION_ICON: Record<IPayerLane['direction'], string> = {
    Outbound: mdiArrowRight,
    'Two-way': mdiSwapHorizontal,
    Inbound: mdiArrowLeft,
};

function SectionHeader({
    title,
    meta,
    action,
}: {
    title: string;
    meta?: string;
    action?: React.ReactNode;
}): React.ReactElement {
    return (
        <S.SectionHeader>
            <S.SectionTitle>{title}</S.SectionTitle>
            {action && <S.SectionAction>{action}</S.SectionAction>}
            {meta && <S.SectionMeta>{meta}</S.SectionMeta>}
        </S.SectionHeader>
    );
}

/**
 * The EMR payer identifiers that map into this payer.
 *
 * The design puts the originating system under each one ("ModMed · payer 3102"). A tag
 * carries a name and nothing else, so that line is left out rather than guessed at — an
 * operator reading a source would act on it. It is ask 5 in the backend doc.
 */
const EmrPayers = observer(
    ({
        emrTags,
        action,
        renderTagAction,
    }: {
        emrTags: IPanelEmrTag[];
        /** The host's "+ Map EMR payer" control, or nothing in a read-only app. */
        action?: React.ReactNode;
        /** Per-tag control, e.g. unmap. Absent in a read-only app. */
        renderTagAction?: (tag: IPanelEmrTag) => React.ReactNode;
    }): React.ReactElement => (
        <>
            <SectionHeader title="EMR payers mapped in" action={action} />
            {emrTags.length === 0 ? (
                <S.Empty>No EMR payer maps into this payer yet.</S.Empty>
            ) : (
                emrTags.map(tag => (
                    <S.Card key={tag.id || tag.name}>
                        <S.CardRow>
                            <S.CardIcon>
                                <Icon path={mdiOfficeBuildingOutline} size={0.7} />
                            </S.CardIcon>
                            <S.CardText>
                                <S.CardTitle title={tag.name}>{tag.name}</S.CardTitle>
                            </S.CardText>
                            {renderTagAction?.(tag)}
                        </S.CardRow>
                    </S.Card>
                ))
            )}
        </>
    ),
);

function ClearinghouseCard({ binding }: { binding: IRoutableConnection }): React.ReactElement {
    const supported = binding.transactions || [];
    const name = binding.payer?.name || binding.clearinghouse?.name || 'Clearinghouse payer';

    return (
        <S.Card>
            <S.CardRow>
                <S.CardIcon>
                    <Icon path={mdiShareVariantOutline} size={0.7} />
                </S.CardIcon>
                <S.CardText>
                    <S.CardTitle title={name}>{name}</S.CardTitle>
                    <S.CardSub>
                        {`${binding.clearinghouse?.name || 'Clearinghouse'} · EDI ${
                            binding.externalId || '—'
                        } · ${supported.length} tx`}
                    </S.CardSub>
                </S.CardText>
                <S.CardMeta>{`${supported.length} of ${EDI_GRID.length} supported`}</S.CardMeta>
            </S.CardRow>
            <S.EdiGrid>
                {EDI_GRID.map(tile => {
                    const isSupported = supported.includes(tile.transaction);

                    return (
                        <S.EdiTile key={tile.transaction} supported={isSupported}>
                            <S.EdiTileIcon supported={isSupported}>
                                <Icon path={isSupported ? mdiCheckCircleOutline : mdiMinus} size={0.65} />
                            </S.EdiTileIcon>
                            <S.EdiTileText>
                                <S.EdiCode supported={isSupported}>{tile.transaction}</S.EdiCode>
                                <S.EdiLabel supported={isSupported}>{tile.label}</S.EdiLabel>
                            </S.EdiTileText>
                        </S.EdiTile>
                    );
                })}
            </S.EdiGrid>
        </S.Card>
    );
}

/**
 * The five `PayerContact` purposes and the paper channel set for each.
 *
 * These are the drop-to-paper paths, a different model from the nine lanes: a purpose is
 * where post goes, a lane is a kind of data that may or may not need post at all.
 */
const DropToPaperPaths = observer(({ payer }: { payer: IRoutablePayer }): React.ReactElement => {
    const paths = DATA_KINDS.map(kind => ({ kind, paper: getPaperPath(payer.contact, kind) }));
    const set = paths.filter(p => p.paper).length;

    return (
        <>
            <SectionHeader title="Drop-to-paper paths" meta={`${set} of ${paths.length} set`} />
            {paths.map(({ kind, paper }) => (
                <S.Card key={kind}>
                    <S.CardRow>
                        <S.CardText>
                            <S.CardTitle>
                                <S.InlineIcon>
                                    <Icon path={DATA_KIND_ICON[kind]} size={0.6} />
                                </S.InlineIcon>
                                {DATA_KIND_PATH_LABEL[kind]}
                                {/* X12 has no transaction for an appeal, an overpayment or
                                    general correspondence, so those three are paper by
                                    definition rather than by configuration (NS-7). Without
                                    saying so, paper routing reads as something we failed to
                                    set up, and someone asks us to "fix" it. */}
                                {isPaperOnlyKind(kind) && <S.Badge>ALWAYS PAPER</S.Badge>}
                            </S.CardTitle>
                            <S.CardSub title={paper?.destination}>
                                {paper ? paper.destination : 'Not configured'}
                            </S.CardSub>
                        </S.CardText>
                        {paper ? <PathChip path={paper.path} /> : <S.CardMeta>None</S.CardMeta>}
                    </S.CardRow>
                </S.Card>
            ))}
        </>
    );
});

/**
 * @param loading the clearinghouse join has not resolved yet.
 *
 * It has to be passed in, because `getPayerLanes` cannot distinguish the two cases in its
 * return value: for undefined bindings it yields `Not routed` for every lane that needs a
 * connection to answer, which is indistinguishable from a payer that genuinely has none. This
 * panel rendered that straight into a "Not routed" chip — telling a support operator that a
 * payer's eligibility checks, claim statuses, authorizations and remittance are unrouted
 * because a request was still in flight. That is a false statement about where real claims go,
 * and acting on it re-routes them (NS-3). The provider panel already guarded this; this one
 * did not, and the gap was found by diffing the two.
 */
function LaneTable({ lanes, loading }: { lanes: IPayerLane[]; loading: boolean }): React.ReactElement {
    return (
        <S.LaneTable>
            <S.LaneRow header>
                <S.LaneHeadCell>Data</S.LaneHeadCell>
                <S.LaneHeadCell>Direction</S.LaneHeadCell>
                <S.LaneHeadCell>Path</S.LaneHeadCell>
                <S.LaneHeadCell>Goes to</S.LaneHeadCell>
            </S.LaneRow>
            {lanes.map(lane => {
                // Only the lanes that NEED a connection to answer are unknown while it loads.
                // A paper-by-definition lane resolves either way — an appeal goes by post
                // whatever the clearinghouse supports (NS-7) — so it keeps its chip.
                const unknown = loading && lane.path === 'Not routed' && !!lane.detail;

                return (
                    <S.LaneRow key={lane.id}>
                        <S.LaneCell>
                            <S.LaneLabel>{lane.label}</S.LaneLabel>
                            <S.LaneSub>{lane.sub}</S.LaneSub>
                        </S.LaneCell>
                        <S.Direction>
                            <Icon path={DIRECTION_ICON[lane.direction]} size={0.6} />
                            {lane.direction}
                        </S.Direction>
                        <div>
                            {unknown ? <S.LaneSub>{UNKNOWN_PATH}</S.LaneSub> : <PathChip path={lane.path} />}
                        </div>
                        <S.LaneCell>
                            {!unknown && lane.destination && (
                                <S.LaneSub title={lane.destination}>{lane.destination}</S.LaneSub>
                            )}
                            <S.LaneSub title={lane.detail}>{unknown ? '' : lane.detail}</S.LaneSub>
                        </S.LaneCell>
                    </S.LaneRow>
                );
            })}
        </S.LaneTable>
    );
}

/** The payer fields this panel renders, beyond the ones routing already reads. */
export interface IPanelPayer extends IRoutablePayer {
    /** The ENTER payer ID — the GUID. Always present, and what operators paste elsewhere. */
    id?: string;
    coverage?: string;
    halt?: boolean;
}

/** An EMR payer identifier mapped into this payer. Both apps' tag models satisfy this. */
export interface IPanelEmrTag {
    id?: string;
    name?: string;
}

export interface IPayerRoutingPanelProps {
    payer: IPanelPayer;
    /** `undefined` while the clearinghouse join is in flight — NOT "no connections". */
    bindings: IRoutableConnection[] | undefined;
    /** Already filtered by the host: support excludes non-INTEGRATION tags; provider has none. */
    emrTags: IPanelEmrTag[];
    /** "+ Map EMR payer", or nothing in a read-only app. */
    emrAction?: React.ReactNode;
    /** Per-tag control such as unmap. Absent in a read-only app. */
    renderEmrTagAction?: (tag: IPanelEmrTag) => React.ReactNode;
    /** "Edit payer" and friends. Absent in a read-only app. */
    footer?: React.ReactNode;
}

/**
 * The expanded row: everything known about one payer's routing.
 *
 * ONE component, rendered by the support console and the provider app. It was two — support's
 * `payer-detail` and provider's `payer-routing-detail` — which is why the two screens drifted
 * on header casing, badge wrapping and section order, and why every fix had to be made twice.
 *
 * The support version is the reference; this is it, with its write controls lifted into slots.
 * They are NODES, not callbacks: a read-only app cannot render a control it never constructed,
 * so provider cannot grow an Edit button by accident (@John Russell, 2026-08-25).
 */
export const PayerRoutingPanel = observer(
    ({
        payer,
        bindings,
        emrTags,
        emrAction,
        renderEmrTagAction,
        footer,
    }: IPayerRoutingPanelProps): React.ReactElement => {
        const lanes = getPayerLanes(payer, bindings);
        const routed = countRouted(lanes);

        return (
            <S.Panel>
                {/* The ENTER payer ID, copyable, before anything else — it is the thing an
                    operator came here to fetch when setting up a Chargemaster or chasing an
                    affiliated payer from a claim. */}
                <S.IdRow>
                    <PayerIdChip id={payer.id} />
                </S.IdRow>
                <S.TopGrid>
                    <S.Column>
                        <EmrPayers emrTags={emrTags} action={emrAction} renderTagAction={renderEmrTagAction} />
                        <SectionHeader title="Clearinghouse payer" />
                        {bindings === undefined ? (
                            <S.Empty>Loading clearinghouse connections…</S.Empty>
                        ) : bindings.length === 0 ? (
                            <S.Empty>No clearinghouse connection — this payer is paper only.</S.Empty>
                        ) : (
                            bindings.map(binding => (
                                <ClearinghouseCard key={binding.externalId || binding.payer?.name} binding={binding} />
                            ))
                        )}
                        {/* The design puts TRANSACTION / SERVED BY here as well as in the
                            drawer. It is the only view that answers "can this payer take an
                            eligibility check at all" when two connections each carry part of
                            the set — the per-connection chips cannot. */}
                        <SectionHeader title="Transaction coverage" />
                        <PayerTransactionTable
                            coverage={getTransactionCoverage(bindings)}
                            loading={bindings === undefined}
                        />
                    </S.Column>
                    <S.Column>
                        <DropToPaperPaths payer={payer} />
                    </S.Column>
                </S.TopGrid>

                <SectionHeader
                    title="Where each kind of data goes"
                    meta={
                        bindings === undefined
                            ? 'checking connections…'
                            : `${routed.routed} of ${routed.total} routed`
                    }
                />
                <LaneTable lanes={lanes} loading={bindings === undefined} />

                {footer && <S.Footer>{footer}</S.Footer>}
            </S.Panel>
        );
    },
);
